import React, { useEffect, useRef } from 'react';
import { useParams, Outlet } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Spin, Result, Button } from 'antd';
import { useGetStorefrontConfigQuery } from '@sama-storefront/features/storefront/api/storefrontApi';
import {
  setSubdomain,
  setStoreConfig,
} from '@sama-storefront/features/storefront/slice/storefrontSlice';

export const StorefrontRoute: React.FC = () => {
  const { subdomain: paramSubdomain } = useParams<{ subdomain: string }>();
  const dispatch = useDispatch();

  const resolvedSubdomain =
    paramSubdomain || window.location.hostname.split('.')[0] || 'sassano-grill';

  useEffect(() => {
    if (resolvedSubdomain) {
      dispatch(setSubdomain(resolvedSubdomain));
    }
  }, [resolvedSubdomain, dispatch]);

  const isFirstLoadRef = useRef(true);

  const {
    data: config,
    isLoading,
    isError,
  } = useGetStorefrontConfigQuery(resolvedSubdomain, {
    skip: !resolvedSubdomain,
    pollingInterval: 5 * 60_000, // 5min - antes 60s, evita refresco visible continuo
    refetchOnFocus: false, // evita refetch al cambiar de pestaña (causaba flash)
    refetchOnReconnect: true,
  });

  useEffect(() => {
    if (config) {
      if (isFirstLoadRef.current) {
        console.debug('[Storefront polling] carga inicial config', {
          subdomain: resolvedSubdomain,
          at: new Date().toISOString(),
        });
        isFirstLoadRef.current = false;
      } else {
        console.debug('[Storefront polling] config refrescada por polling (5min)', {
          subdomain: resolvedSubdomain,
          at: new Date().toISOString(),
          partnerId: config.partner?.id,
        });
      }
      dispatch(setStoreConfig(config));
    }
  }, [config, dispatch, resolvedSubdomain]);

  if (isLoading) {
    return (
      <div
        style={{
          minHeight: '100dvh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'var(--color-paper)',
          color: 'var(--color-ink)',
        }}
      >
        <Spin size="large" />
        <p style={{ marginTop: 16, color: 'var(--color-muted)', fontWeight: 500 }}>
          Cargando tienda...
        </p>
      </div>
    );
  }

  if (isError || !config) {
    return (
      <div
        style={{
          minHeight: '100dvh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'var(--color-paper)',
        }}
      >
        <Result
          status="404"
          title={<span style={{ color: 'var(--color-ink)' }}>Tienda no encontrada</span>}
          subTitle={
            <span style={{ color: 'var(--color-muted)' }}>
              La tienda "{resolvedSubdomain}" no está disponible o el enlace es incorrecto.
            </span>
          }
          extra={
            <Button type="primary" onClick={() => (window.location.href = '/')}>
              Volver al inicio
            </Button>
          }
        />
      </div>
    );
  }

  return <Outlet />;
};

export default StorefrontRoute;
